/* eslint-disable no-plusplus */
import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import Map, {
    Layer, Marker, Popup, Source,
} from 'react-map-gl';
import * as turf from '@turf/turf';
import WebMercatorViewport from '@math.gl/web-mercator';
import OriginIcon from './img/origin.svg';
import DestinationIcon from './img/destination.svg';
import ScanSummaryCard from '../scanSummaryCard/ScanSummaryCard';
import { getMapStyle, defaultMapTheme, MAP_THEMES } from '../../config/function/mapStyles';
import {
    CHARGE_COLOR, EVENT_COLOR, FUEL_COLOR, IDLE_COLOR, PROGRESS_COLOR, ROUTE_COLOR,
} from './mapColors';
import {
    EMPTY_FC, matchColor, pointsToFeatureCollection, tripsToFeatureCollection,
} from './mapFeatures';
import './mapTripReport.css';

const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;

// Kuala Lumpur — the fallback when a trip carries no usable geometry.
const FALLBACK_VIEW = { longitude: 101.58714, latitude: 3.08587, zoom: 10 };
const BLE_POPUP_THRESHOLD_M = 10;

/**
 * Zoom the camera settles at when a Safety or Idle row pins a point.
 *
 * The prototype's `focusEvent` uses 15.5, which frames a neighbourhood — too wide to see which
 * side of a junction an event fired on, which is the question that made you click the row. One
 * number so it stays a knob rather than a literal buried in an effect.
 */
const FOCUS_ZOOM = 16.5;

/**
 * The wording for one overlay row, shared by the hover popup and the pinned focus popup.
 *
 * Both say the same thing about the same row, and the only difference is what put them on screen,
 * so they must not drift into two descriptions of one marker.
 */
const PointCard = ({ point }) => (
    <div className="od-focus-card">
        <div className="od-focus-title">{point.title}</div>
        {point.when ? <div className="od-focus-when">{point.when}</div> : null}
        {point.trip ? <div className="od-focus-trip">{point.trip}</div> : null}
        {point.lines?.length > 0 && <div className="od-focus-lines">{point.lines.join(' · ')}</div>}
        {point.address ? <div className="od-focus-address">{point.address}</div> : null}
    </div>
);

/** Layers whose features identify a trip. Everything else in `properties.id` is a row id. */
const ROUTE_LAYERS = new Set(['route', 'route-highlight', 'sel-line']);

/**
 * Overlay layers a click can pick a row out of, and the row set each one indexes.
 *
 * Ordered by how small the target is: a fuel dot is drawn under the event and idle dots and is the
 * largest of the three, so it yields to them when a click lands on both.
 */
const PICKABLE_LAYERS = [
    { layer: 'events-pt', kind: 'event' },
    { layer: 'idle-pt', kind: 'idle' },
    { layer: 'fuel-pt', kind: 'fuel' },
    { layer: 'charge-pt', kind: 'charge' },
];

/**
 * originGeo / destinationGeo arrive as "lat, lon" TEXT — latitude first, the opposite
 * axis order to tripGeo.coordinates. Kept local to the map so callers never juggle it.
 */
const parseLatLonText = (value) => {
    if (typeof value !== 'string') return null;
    const parts = value.split(',');
    if (parts.length !== 2) return null;
    const lat = Number(parts[0].trim());
    const lon = Number(parts[1].trim());
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
    return [lon, lat];
};

/**
 * Route map for O/D Analytics.
 *
 * Draws every visible trip, highlights the hovered one, isolates the selected one, and
 * pins origin/destination. Selection and hover are driven entirely by props so the trip
 * rail and the map stay in sync in both directions.
 */
export default function MapTripReport(props) {
    const {
        trips,
        selectedTrip,
        hoverTripId,
        onHoverTrip,
        onSelectTrip,
        showTripNumbers,
        showOdMarkers,
        mapTheme = defaultMapTheme,
        events,
        episodes,
        fuelEvents,
        chargeSessions,
        onPickPoint,
        describePoint,
        progressGeo,
        progressTip,
        cursor,
        followCursor,
        focus,
        onClearFocus,
    } = props;

    const mapRef = useRef();
    const containerRef = useRef(null);
    const mapStyle = getMapStyle(mapTheme);
    /**
     * Which style URL the overlays below have been cleared to draw against — not a bare boolean.
     *
     * A boolean only ever set true went stale the moment the basemap changed, and switching to
     * satellite threw `Style is not done loading` from every Source. mapbox-gl v3 builds a
     * Studio Standard style out of *imports*, and the two readiness checks disagree about them:
     * `map.isStyleLoaded()` walks `style.fragments` and waits, but react-map-gl's Source guards
     * on the root `map.style._loaded` alone (react-map-gl/dist/esm/components/source.js). So
     * between the root resolving and its fragments landing, react-map-gl re-adds every source,
     * and `addSource` serializes the imports to validate them and throws on the unloaded one.
     *
     * Comparing URLs makes the gate false in the very render the theme changes, so the sources
     * unmount before that window opens. A `useEffect` reset would not: react-map-gl's own effect
     * belongs to a child and runs first, calling setStyle while the gate still read true.
     */
    const [loadedStyle, setLoadedStyle] = useState(null);
    const isStyleLoaded = loadedStyle === mapStyle;
    const [viewState, setViewState] = useState({ ...FALLBACK_VIEW, zoom: 10 });
    const [bleHover, setBleHover] = useState(null);
    const [hoverPoint, setHoverPoint] = useState(null);
    // Dismissed rather than auto-cleared: mapbox-gl re-fires onError per failed tile, so a
    // self-clearing banner would flicker for the whole pan.
    const [mapError, setMapError] = useState(null);

    const isSatellite = mapTheme === MAP_THEMES.SATELLITE;
    const hasSelection = Boolean(selectedTrip);
    const hasHover = Boolean(hoverTripId);
    // From mapColors so the Route Map's legend cannot drift from what the map actually paints.
    const routeColor = isSatellite ? ROUTE_COLOR.satellite : ROUTE_COLOR.monochrome;
    const progressColor = isSatellite ? PROGRESS_COLOR.satellite : PROGRESS_COLOR.monochrome;

    // ---- sources -------------------------------------------------------------

    // With a trip selected the overview lines are dropped entirely so the single route
    // reads clearly; otherwise every visible trip is drawn at reduced opacity.
    const overviewData = useMemo(
        () => (hasSelection ? EMPTY_FC : tripsToFeatureCollection(trips)),
        [trips, hasSelection],
    );

    const selectedData = useMemo(
        () => (hasSelection ? tripsToFeatureCollection([selectedTrip]) : EMPTY_FC),
        [selectedTrip, hasSelection],
    );

    /**
     * Overlays follow the SELECTION, not the deck's "selected trip only" toggle: the map is showing
     * one route, so plotting other trips' events on it would attribute them to the wrong journey.
     * With nothing selected the whole window is drawn, which is how you find the bad trip.
     */
    const eventData = useMemo(() => {
        const rows = hasSelection
            ? (events || []).filter((row) => row.tripId === selectedTrip.startTraceId)
            : events;
        return pointsToFeatureCollection(rows, (row) => ({
            id: row.id, type: row.type, tripId: row.tripId || '',
        }));
    }, [events, hasSelection, selectedTrip]);

    /**
     * Refuels and drains, scoped like the other overlays.
     *
     * A ledger event that happened between trips carries `tripId: null` and so belongs to no
     * route; with a trip selected it is filtered out rather than drawn over whichever route is on
     * screen, which would attribute a depot fill to a journey it has nothing to do with.
     */
    const fuelData = useMemo(() => {
        const rows = hasSelection
            ? (fuelEvents || []).filter((row) => row.tripId === selectedTrip.startTraceId)
            : fuelEvents;
        return pointsToFeatureCollection(
            (rows || []).filter((row) => FUEL_COLOR[row.type] !== undefined),
            (row) => ({ id: row.id, type: row.type }),
        );
    }, [fuelEvents, hasSelection, selectedTrip]);

    /** Charging sessions, scoped like the fuel events and for the same reason. */
    const chargeData = useMemo(() => {
        const rows = hasSelection
            ? (chargeSessions || []).filter((row) => row.tripId === selectedTrip.startTraceId)
            : chargeSessions;
        return pointsToFeatureCollection(rows, (row) => ({ id: row.id }));
    }, [chargeSessions, hasSelection, selectedTrip]);

    const idleData = useMemo(() => {
        const rows = hasSelection
            ? (episodes || []).filter((row) => row.tripId === selectedTrip.startTraceId)
            : episodes;
        // Only the classes the legend names — see IDLE_COLOR on why plain 400 is left off.
        return pointsToFeatureCollection(
            (rows || []).filter((row) => IDLE_COLOR[row.classCode] !== undefined),
            (row) => ({ id: row.id, classCode: String(row.classCode) }),
        );
    }, [episodes, hasSelection, selectedTrip]);

    const odMarkers = useMemo(() => {
        let source = [];
        if (hasSelection) source = [selectedTrip];
        else if (showOdMarkers) source = trips;
        return (source || []).flatMap((trip, index) => {
            const origin = parseLatLonText(trip?.originGeo);
            const destination = parseLatLonText(trip?.destinationGeo);
            const markers = [];
            if (origin) {
                markers.push({
                    key: `o-${trip.startTraceId || index}`, kind: 'origin', lon: origin[0], lat: origin[1], label: trip.origin,
                });
            }
            if (destination) {
                markers.push({
                    key: `d-${trip.startTraceId || index}`, kind: 'destination', lon: destination[0], lat: destination[1], label: trip.destination,
                });
            }
            return markers;
        });
    }, [trips, selectedTrip, hasSelection, showOdMarkers]);

    // Trip-number pins sit at the start of each polyline, so they land on the route even
    // when originGeo is null.
    const numberMarkers = useMemo(() => {
        if (hasSelection || !showTripNumbers) return [];
        return (trips || []).reduce((acc, trip, index) => {
            const first = trip?.tripGeo?.coordinates?.[0];
            if (Array.isArray(first) && first.length >= 2) {
                acc.push({
                    key: trip.startTraceId || `n-${index}`, n: index + 1, lon: first[0], lat: first[1],
                });
            }
            return acc;
        }, []);
    }, [trips, showTripNumbers, hasSelection]);

    // ---- layers -------------------------------------------------------------

    const overviewLayer = useMemo(() => ({
        id: 'route',
        type: 'line',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
            'line-color': routeColor,
            'line-opacity': hasHover ? 0.35 : 0.6,
            'line-width': 4,
        },
    }), [routeColor, hasHover]);

    const overviewHaloLayer = useMemo(() => ({
        id: 'route-highlight-halo',
        type: 'line',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
            'line-color': '#FFFFFF',
            'line-width': isSatellite ? 10 : 8,
            'line-blur': 0.5,
        },
    }), [isSatellite]);

    const overviewHighlightLayer = useMemo(() => ({
        id: 'route-highlight',
        type: 'line',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
            'line-color': routeColor,
            'line-opacity': 1,
            'line-width': isSatellite ? 6 : 5,
        },
    }), [routeColor, isSatellite]);

    const selectedCasingLayer = useMemo(() => ({
        id: 'sel-casing',
        type: 'line',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': '#FFFFFF', 'line-width': 9, 'line-opacity': 0.9 },
    }), []);

    const selectedLineLayer = useMemo(() => ({
        id: 'sel-line',
        type: 'line',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
            'line-color': routeColor,
            'line-opacity': 1,
            'line-width': 5,
        },
    }), [routeColor]);

    const hoverFilter = useMemo(() => ['==', ['get', 'id'], hoverTripId || ''], [hoverTripId]);

    // Radii ramp with zoom so a city-wide view stays readable and a street-level one is precise.
    const idleLayer = useMemo(() => ({
        id: 'idle-pt',
        type: 'circle',
        paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 3, 14, 6],
            'circle-color': matchColor('classCode', IDLE_COLOR),
            'circle-opacity': 0.85,
            'circle-stroke-width': 1.2,
            'circle-stroke-color': '#FFFFFF',
        },
    }), []);

    // The travelled portion, over the selected route. Two layers on two sources so the growing
    // one keeps a stable identity between sample crossings — see progressGeo in RouteMapView.
    const progressLayer = useMemo(() => ({
        id: 'progress-line',
        type: 'line',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': progressColor, 'line-width': 5 },
    }), [progressColor]);

    const progressTipLayer = useMemo(() => ({
        id: 'progress-tip',
        type: 'line',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': progressColor, 'line-width': 5 },
    }), [progressColor]);

    // Above the idle dots: a harsh brake inside a long idle is the thing you are looking for.
    /**
     * Bigger and more heavily ringed than an event or idle dot, and drawn beneath them.
     *
     * A ledger event is rare — a handful in a window against hundreds of harsh-braking dots — and
     * it is the one mark a reader goes looking for rather than stumbles on, so it has to survive
     * sitting in a cluster of them.
     */
    const fuelLayer = useMemo(() => ({
        id: 'fuel-pt',
        type: 'circle',
        paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 4, 14, 7],
            'circle-color': matchColor('type', FUEL_COLOR),
            'circle-opacity': 0.9,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#FFFFFF',
        },
    }), []);

    /** Sized with the fuel dots — both are ledger marks a reader goes looking for. */
    const chargeLayer = useMemo(() => ({
        id: 'charge-pt',
        type: 'circle',
        paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 4, 14, 7],
            'circle-color': CHARGE_COLOR,
            'circle-opacity': 0.9,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#FFFFFF',
        },
    }), []);

    const eventLayer = useMemo(() => ({
        id: 'events-pt',
        type: 'circle',
        paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 4, 14, 7],
            'circle-color': matchColor('type', EVENT_COLOR),
            'circle-stroke-width': 1.6,
            'circle-stroke-color': '#FFFFFF',
        },
    }), []);

    // ---- camera -------------------------------------------------------------

    /**
     * Fit to a bbox using the real container size. The previous implementation hardcoded
     * a 300x600 viewport, so every fit was computed for a box that was not on screen and
     * the framing was consistently wrong.
     */
    const fitToBbox = useCallback((bbox) => {
        try {
            const node = containerRef.current;
            const width = Math.max(node?.clientWidth || 0, 80);
            const height = Math.max(node?.clientHeight || 0, 80);
            const viewport = new WebMercatorViewport({ width, height });
            // fitBounds wants [[lon, lat], [lon, lat]]; turf.bbox gives [minX, minY, maxX, maxY].
            const bounds = [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
            const padding = Math.min(56, Math.floor(Math.min(width, height) / 6));
            const { longitude, latitude, zoom } = viewport.fitBounds(bounds, { padding });
            if (!Number.isFinite(longitude) || !Number.isFinite(latitude) || !Number.isFinite(zoom)) {
                return FALLBACK_VIEW;
            }
            // A single-point bbox fits at absurd zoom; cap it at street level.
            return { longitude, latitude, zoom: Math.min(zoom, 16) };
        } catch (error) {
            return FALLBACK_VIEW;
        }
    }, []);

    /**
     * mapbox-gl's own `trackResize` only listens for the WINDOW resize event (see
     * Map._onWindowResize), so a container that changes height on its own — the tape and deck
     * rows appearing when a trip is selected shrink the map row — leaves the canvas at its
     * mount-time size and the rest of the container shows as a blank strip.
     */
    useEffect(() => {
        const node = containerRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => mapRef.current?.resize());
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const flyToBbox = useCallback((featureCollection) => {
        if (!featureCollection?.features?.length) return;
        try {
            const target = fitToBbox(turf.bbox(featureCollection));
            setViewState((previous) => ({ ...previous, ...target }));
            mapRef.current?.flyTo({
                center: [target.longitude, target.latitude], zoom: target.zoom, duration: 700,
            });
        } catch (error) {
            // A malformed geometry is not worth breaking the view over.
        }
    }, [fitToBbox]);

    // Read by the two camera effects below without making them depend on the focus itself.
    const focusRef = useRef(focus);
    focusRef.current = focus;

    /**
     * Frame the selected trip, or the whole visible set when nothing is selected.
     *
     * A pinned point suppresses the fit that would otherwise accompany its own trip selection —
     * the two happen in one commit, and effect order is not a contract worth betting a camera on.
     * Read through a ref deliberately: with `focus` as a dependency, *clearing* a pin would re-run
     * this and fly out to the whole trip, so scrubbing away from a pinned event would zoom the map
     * out from under the reader.
     */
    useEffect(() => {
        if (!isStyleLoaded || focusRef.current) return;
        flyToBbox(hasSelection ? selectedData : overviewData);
    }, [isStyleLoaded, hasSelection, selectedData, overviewData, flyToBbox]);

    // Fly to the pinned point, never zooming further out than the reader already is.
    useEffect(() => {
        if (!isStyleLoaded || !focus) return;
        if (!Number.isFinite(focus.lat) || !Number.isFinite(focus.lon)) return;
        const zoom = Math.max(mapRef.current?.getZoom?.() ?? 0, FOCUS_ZOOM);
        setViewState((previous) => ({
            ...previous, longitude: focus.lon, latitude: focus.lat, zoom,
        }));
        mapRef.current?.flyTo({ center: [focus.lon, focus.lat], zoom, duration: 700 });
    }, [focus, isStyleLoaded]);

    // Armed by the first cursor of each trip and read by the follow effect below.
    const following = useRef(false);
    useEffect(() => { following.current = false; }, [selectedTrip?.startTraceId]);

    /**
     * Keep the vehicle pinned to the centre and move the map under it.
     *
     * `jumpTo`, not `easeTo`: the cursor advances on the tape's rAF loop, so an animated camera
     * would be superseded 16ms into its own 400ms ease and never complete — that is the jitter the
     * earlier safe-box compromise was avoiding. A jump per frame has no animation to interrupt, so
     * the marker sits still at the centre and the map slides beneath it.
     *
     * **A pinned point wins outright.** Clicking a Safety or Idle row is an instruction to look at
     * one place, and the vehicle is somewhere else on the route entirely — following it would drag
     * the reader off the point they asked for, which is what the trail landing a beat after the map
     * used to do. Driving the tape clears the pin upstream, so playing or scrubbing follows again.
     *
     * **The first cursor of a trip is skipped** even without a pin, because it is the tape
     * initialising rather than the vehicle moving: the tape parks its playhead at the trip's start
     * when a trail arrives, and following that commit throws away the bbox fit the map just made.
     */
    useEffect(() => {
        if (!followCursor || !cursor || !isStyleLoaded || focus) return;
        if (!Number.isFinite(cursor.lat) || !Number.isFinite(cursor.lon)) return;
        if (!following.current) {
            following.current = true;
            return;
        }
        mapRef.current?.jumpTo?.({ center: [cursor.lon, cursor.lat] });
    }, [cursor, followCursor, isStyleLoaded, focus, selectedTrip?.startTraceId]);

    // ---- interaction --------------------------------------------------------

    /** The topmost overlay row under the pointer, as `{kind, id, lat, lon}`, or null. */
    const pickOverlay = useCallback((features) => {
        const hit = PICKABLE_LAYERS.find(({ layer }) => (features || [])
            .some((candidate) => candidate.layer?.id === layer));
        if (!hit) return null;
        const feature = features.find((candidate) => candidate.layer?.id === hit.layer);
        const [lon, lat] = feature.geometry?.coordinates || [];
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
        return {
            kind: hit.kind, id: feature.properties?.id, lat, lon,
        };
    }, []);

    const handleMouseMove = useCallback((event) => {
        // Only the route layers carry a trip id in `properties.id`; on an event or idle dot that
        // field is the row's own id, and hovering one would set a hoverTripId matching no trip.
        const feature = (event.features || []).find((candidate) => ROUTE_LAYERS.has(candidate.layer?.id));
        const id = feature?.properties?.id || null;
        if (typeof onHoverTrip === 'function') onHoverTrip(id);
        setBleHover(id ? event.lngLat : null);

        // Identity-compared before committing: mousemove fires per pixel, and a dot is many
        // pixels wide, so re-rendering the map for every one of them inside a single marker is
        // the difference between a popup and a stutter.
        const next = pickOverlay(event.features);
        setHoverPoint((previous) => {
            if (previous?.kind === next?.kind && previous?.id === next?.id) return previous;
            return next;
        });
    }, [onHoverTrip, pickOverlay]);

    const handleMouseLeave = useCallback(() => {
        if (typeof onHoverTrip === 'function') onHoverTrip(null);
        setBleHover(null);
        setHoverPoint(null);
    }, [onHoverTrip]);

    const selectTripById = useCallback((tripId) => {
        if (!tripId || typeof onSelectTrip !== 'function') return;
        const trip = (trips || []).find((candidate) => candidate.startTraceId === tripId);
        if (trip) onSelectTrip(trip);
    }, [trips, onSelectTrip]);

    /**
     * A dot wins over the route beneath it — it is the smaller target, so a click that hits both
     * was aimed at the dot. The map reports only which row was hit; the container looks it up and
     * decides what to show, because the popup's wording needs the whole row and a map feature
     * carries only what the paint expressions need.
     */
    const handleClick = useCallback((event) => {
        const features = event.features || [];

        const hit = pickOverlay(features);
        if (hit) {
            if (typeof onPickPoint === 'function') onPickPoint({ kind: hit.kind, id: hit.id });
            return;
        }

        const routeFeature = features.find((candidate) => ROUTE_LAYERS.has(candidate.layer?.id));
        selectTripById(routeFeature?.properties?.id);
    }, [onPickPoint, pickOverlay, selectTripById]);

    /**
     * `isStyleLoaded()` is the check that waits for imports, so it stays the gate — but the last
     * `styledata` of a switch can fire while a fragment is still in flight, which would leave the
     * overlays off for good. `idle` cannot: it means loaded *and* drawn, so it always arrives.
     */
    const handleStyleLoaded = useCallback((event) => {
        // Off the event, not the ref: `idle` can fire before the ref is attached on first load.
        const map = event?.target || mapRef.current?.getMap?.();
        if (map?.isStyleLoaded?.()) setLoadedStyle(mapStyle);
    }, [mapStyle]);

    /**
     * Without this a missing REACT_APP_MAPBOX_TOKEN or a blocked tile host renders a silent grey
     * box with the controls still floating on top, and nothing tells the reader that the tables
     * below are unaffected.
     */
    const handleMapError = useCallback((event) => {
        setMapError(mapboxToken
            ? 'Map tiles are unreachable. Every panel below still reads from the trip data.'
            : 'Map unavailable — no Mapbox token is configured for this build. Every panel below still reads from the trip data.');
        // eslint-disable-next-line no-console
        console.warn('mapTripReport: map error', event?.error || event);
    }, []);

    /**
     * The hovered row's own readout, or null.
     *
     * `describePoint` is a lookup-and-format callback rather than a piece of state the container
     * pushes down: hover changes as fast as the pointer moves, and round-tripping every marker
     * through the container's state to get its wording back would re-render the whole panel.
     */
    const hoverDetail = useMemo(() => {
        if (!hoverPoint || typeof describePoint !== 'function') return null;
        if (focus && focus.lat === hoverPoint.lat && focus.lon === hoverPoint.lon) return null;
        return describePoint(hoverPoint);
    }, [hoverPoint, describePoint, focus]);

    /** Closest BLE beacon scan to the cursor, if one is within BLE_POPUP_THRESHOLD_M. */
    const nearestBleEntry = useMemo(() => {
        if (!bleHover || !hoverTripId) return null;
        const trip = (trips || []).find((candidate) => candidate.startTraceId === hoverTripId);
        const span = (trip?.bleSpan || []).filter((e) => e.latitude != null && e.longitude != null);
        if (span.length === 0) return null;

        const { lat, lng } = bleHover;
        let nearest = span[0];
        let minSqDist = (nearest.latitude - lat) ** 2 + (nearest.longitude - lng) ** 2;
        for (let i = 1; i < span.length; i++) {
            const d = (span[i].latitude - lat) ** 2 + (span[i].longitude - lng) ** 2;
            if (d < minSqDist) {
                minSqDist = d;
                nearest = span[i];
            }
        }
        const distM = turf.distance([lng, lat], [nearest.longitude, nearest.latitude], { units: 'meters' });
        return distM <= BLE_POPUP_THRESHOLD_M ? nearest : null;
    }, [bleHover, hoverTripId, trips]);

    return (
        <div className="od-map-canvas" ref={containerRef} style={{ width: '100%', height: '100%' }}>
            {mapError && (
                <div className="od-map-error" role="alert">
                    <span>{mapError}</span>
                    <button type="button" onClick={() => setMapError(null)} aria-label="Dismiss">×</button>
                </div>
            )}
            <Map
              {...viewState}
              ref={mapRef}
              onMove={(event) => setViewState(event.viewState)}
              onLoad={handleStyleLoaded}
              onStyleData={handleStyleLoaded}
              onIdle={handleStyleLoaded}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
              onError={handleMapError}
              mapStyle={mapStyle}
              interactiveLayerIds={['route', 'route-highlight', 'sel-line', 'events-pt', 'idle-pt', 'fuel-pt', 'charge-pt']}
              mapboxAccessToken={mapboxToken}
              cursor={hasHover || hoverPoint ? 'pointer' : 'grab'}
              style={{ width: '100%', height: '100%' }}
            >
                {isStyleLoaded && (
                    <>
                        <Source id="trips" type="geojson" data={overviewData}>
                            <Layer {...overviewLayer} />
                            <Layer {...overviewHaloLayer} filter={hoverFilter} />
                            <Layer {...overviewHighlightLayer} filter={hoverFilter} />
                        </Source>
                        <Source id="seltrip" type="geojson" data={selectedData}>
                            <Layer {...selectedCasingLayer} />
                            <Layer {...selectedLineLayer} />
                        </Source>
                        <Source id="progress" type="geojson" data={progressGeo || EMPTY_FC}>
                            <Layer {...progressLayer} />
                        </Source>
                        <Source id="progresstip" type="geojson" data={progressTip || EMPTY_FC}>
                            <Layer {...progressTipLayer} />
                        </Source>
                        <Source id="idlepts" type="geojson" data={idleData}>
                            <Layer {...idleLayer} />
                        </Source>
                        <Source id="chargepts" type="geojson" data={chargeData}>
                            <Layer {...chargeLayer} />
                        </Source>
                        <Source id="fuelpts" type="geojson" data={fuelData}>
                            <Layer {...fuelLayer} />
                        </Source>
                        <Source id="events" type="geojson" data={eventData}>
                            <Layer {...eventLayer} />
                        </Source>
                    </>
                )}

                {odMarkers.map((marker) => (
                    <Marker key={marker.key} longitude={marker.lon} latitude={marker.lat} anchor="bottom">
                        <img
                          src={marker.kind === 'origin' ? OriginIcon : DestinationIcon}
                          alt={marker.kind}
                          title={marker.label || marker.kind}
                          loading="lazy"
                        />
                    </Marker>
                ))}

                {numberMarkers.map((marker) => (
                    <Marker key={marker.key} longitude={marker.lon} latitude={marker.lat}>
                        <div className="origin-marker">{marker.n}</div>
                    </Marker>
                ))}

                {cursor && Number.isFinite(cursor.lat) && Number.isFinite(cursor.lon) && (
                    <Marker
                      longitude={cursor.lon}
                      latitude={cursor.lat}
                      rotation={cursor.heading || 0}
                      rotationAlignment="map"
                    >
                        {/* Horizon dark for the disc, as the origin markers use, but the arrow is
                            the one colour on this map that belongs to no category: the vehicle is
                            a position, not a class of event, and every other mark here comes from
                            a ramp that means something. It also has to be findable at a glance
                            while the tape plays, over both basemaps and whatever the route is
                            painted in. It used to borrow ROUTE_COLOR.satellite, which re-tinted
                            the marker on a theme it is not drawn differently on. */}
                        <svg className="veh-marker" width="26" height="26" viewBox="0 0 26 26" aria-hidden="true">
                            <circle cx="13" cy="13" r="11" fill="#26404E" />
                            <path d="M13 5 L19 18 L13 15 L7 18 Z" fill="#10E8CB" />
                        </svg>
                    </Marker>
                )}

                {/* The pinned point: a pulsing ring so it reads as "here" against a route that may
                    already carry a dot at the same place, plus the row's own figures as a popup.
                    The pulse is a CSS animation — the prototype drives it from rAF, repainting a
                    mapbox paint property 60 times a second for what the compositor does free. */}
                {focus && Number.isFinite(focus.lat) && Number.isFinite(focus.lon) && (
                    <>
                        <Marker longitude={focus.lon} latitude={focus.lat}>
                            <span className="od-focus-ring" aria-hidden="true" />
                        </Marker>
                        <Popup
                          latitude={focus.lat}
                          longitude={focus.lon}
                          anchor="bottom"
                          offset={18}
                          closeOnClick={false}
                          onClose={onClearFocus}
                          className="od-focus-popup"
                        >
                            <PointCard point={focus} />
                        </Popup>
                    </>
                )}

                {/* Hovering a dot reads it without committing to it — no ring, no camera move, and
                    it disappears with the pointer. Suppressed over the pinned point, where the
                    focus popup is already open and two stacked cards say the same thing twice. */}
                {hoverDetail && (
                    <Popup
                      latitude={hoverPoint.lat}
                      longitude={hoverPoint.lon}
                      anchor="bottom"
                      offset={14}
                      closeButton={false}
                      closeOnClick={false}
                      className="od-focus-popup od-hover-popup"
                    >
                        <PointCard point={hoverDetail} />
                    </Popup>
                )}

                {nearestBleEntry && bleHover && (
                    <Popup
                      latitude={bleHover.lat}
                      longitude={bleHover.lng}
                      closeButton={false}
                      closeOnClick={false}
                      anchor="bottom"
                      offset={12}
                      className="ble-scan-popup"
                    >
                        <ScanSummaryCard bleSpan={nearestBleEntry} />
                    </Popup>
                )}

                <style>
                    {`
              .mapboxgl-popup-content {
                display: flex;
                box-shadow: 0rem 0.75rem 1rem -0.875rem rgba(16, 24, 40, 0.08), 0rem 0.25rem 0.375rem -0.125rem rgba(16, 24, 40, 0.03);
                border-radius: 0.5rem;
                font-weight: 600;
                font-size: 0.75rem;
                width: 100%;
                text-align: center;
                box-sizing: border-box;
                padding: 0.75rem 1rem;
              }
            `}
                </style>
            </Map>
        </div>
    );
}
